import { api } from '../api/index.js'
import { uploadToSignedUrl } from './uploadService.js'

/**
 * The parts of the CMS that are not about any one content surface: uploading an
 * asset to R2, and reading a record's change history.
 *
 * Both endpoints (`/v1/admin/assets/*`, `/v1/admin/content-history/*`) are
 * scope- and table-keyed precisely so every surface shares them — playbooks
 * first, the Masterclass editor second. They lived on `playbookCmsService`
 * while playbooks were the only caller; reaching into a service named after
 * another surface to upload a masterclass video is how a "shared" seam quietly
 * stops being one.
 */

/** Every image an admin can upload, and where it lands. Mirrors the API allowlist. */
export type ImageAssetScope =
  | 'playbook-thumbnail'
  | 'playbook-video-thumbnail'
  | 'playbook-additional-video-thumbnail'
  | 'coaching-replay'
  | 'masterclass-card-image'
  | 'masterclass-lesson-thumbnail'

export type VideoAssetScope = 'playbook-video' | 'playbook-additional-video' | 'masterclass-lesson-video'

export interface UploadedImage {
  r2_key: string
  preview_url: string
}

export interface ContentHistoryEntry {
  id: string
  op: 'INSERT' | 'UPDATE' | 'DELETE'
  created_at: string
  reason: string | null
  changed_by: string | null
  changed_by_name: string | null
  changed_fields: { field: string; from: unknown; to: unknown }[]
}

export const cmsAssetsService = {
  /** Upload an image; returns the key to PATCH plus a URL to preview now. */
  uploadImage: async (file: File, scope: ImageAssetScope): Promise<UploadedImage> => {
    return api
      .url(`/v1/admin/assets/image?scope=${scope}`)
      .content(file.type || 'application/octet-stream')
      .body(file)
      .post()
      .json<UploadedImage>()
  },

  /**
   * Upload a video straight to R2 via a presigned PUT.
   *
   * The transfer itself is `uploadToSignedUrl`, the same XHR-with-progress
   * helper the file, voice-message and brand-asset uploads use — these files
   * are large enough that a bare spinner is not good enough feedback, and that
   * is exactly what it is for. The PUT must echo the Content-Type that was
   * signed, hence passing `content_type` back rather than `file.type`.
   */
  uploadVideo: async (
    file: File,
    scope: VideoAssetScope,
    onProgress?: (percent: number) => void,
  ): Promise<{ r2_key: string }> => {
    const { r2_key, upload_url, content_type } = await api
      .post({ scope, contentType: file.type }, '/v1/admin/assets/upload-url')
      .json<{ r2_key: string; upload_url: string; content_type: string }>()

    await uploadToSignedUrl(file, upload_url, onProgress, content_type).catch((error: unknown) => {
      const reason = error instanceof Error ? error.message : 'Upload failed'
      // A network-level failure on a direct-to-R2 PUT is nearly always a
      // missing CORS rule for this origin, which is otherwise indistinguishable
      // from the user being offline. Status-carrying failures speak for
      // themselves and pass through.
      throw new Error(
        reason === 'Upload failed'
          ? 'Upload failed — if this persists, check the R2 bucket allows PUT from this origin'
          : reason,
      )
    })

    return { r2_key }
  },

  /** Who changed this row, when, and which columns moved. Allowlisted tables only. */
  history: async (table: string, rowId: string): Promise<ContentHistoryEntry[]> => {
    return api.get(`/v1/admin/content-history/${table}/${rowId}`).json<ContentHistoryEntry[]>()
  },
}
